<template>
  <div class="relative z-10 container-bg flex flex-col text-center items-center p-4 md:p-10 2xl:p-20 m-8 lg:m-12 2xl:m-24">
    <h1 class="mb-11 text-45 text-semantic-base font-medium leading-10">Reset your password</h1>

    <div
      v-if="hasToken && errorMessage && typeof errorMessage === 'string'"
      class="mt-3 bg-red-300 border border-red-400 rounded-xl p-4 md:py-5 md:px-8 font-medium text-sm text-red-400 mb-8"
    >
      <span
        class="icon md:mr-3.5"
        v-html="icons.info"
      ></span>

      {{ errorMessage }}
    </div>

    <p
      v-if="!hasToken"
      class="text-lg text-semantic-muted leading-6 mb-11"
    >
      We will send you a password reset link if your email exists in our system.
    </p>

    <form @submit.prevent="onSubmit" class="text-left w-full max-w-sm">
      <form-field-input
        v-model.trim="email"
        class="mb-8"
        size="medium"
        type="email"
        field-label="E-mail Address *"
        :validation-class="{invalid: $v.email.$error, valid: !$v.email.$invalid}"
        :disabled="hasToken"
        @blur="$v.email.$touch()"
      >
        <template #helper v-if="$v.email.$error">
          <span
            v-if="!$v.email.required"
            class="invalid"
          >
            Email can't be empty
          </span>

            <span
              v-if="!$v.email.email"
              class="invalid"
            >
            Email isn't valid
          </span>
        </template>
      </form-field-input>

      <template
        v-if="hasToken"
      >
        <form-field-input
          v-model.trim="password"
          class="mb-8"
          size="medium"
          type="password"
          field-label="New Password *"
          :validation-class="{invalid: $v.password.$error, valid: !$v.password.$invalid}"
          @blur="$v.password.$touch()"
        >
          <template #helper v-if="$v.password.$error">
            <span
              v-if="!$v.password.required"
              class="invalid"
            >
              Password can't be empty
            </span>

            <span
              v-if="!$v.password.minLength"
              class="invalid"
            >
                min Length is 6
            </span>
          </template>
        </form-field-input>

        <form-field-input
          v-model.trim="password_confirmation"
          class="mb-8"
          size="medium"
          type="password"
          field-label="New Password *"
          :validation-class="{invalid: $v.password_confirmation.$error, valid: $v.password_confirmation.sameAsPassword && !$v.password.$invalid}"
          @blur="$v.password_confirmation.$touch()"
        >
          <template #helper v-if="$v.password_confirmation.$error">
            <span
              v-if="!$v.password_confirmation.required"
              class="invalid"
            >
              Passwords must be identical
            </span>

            <span
              v-if="!$v.password_confirmation.minLength && $v.password_confirmation.sameAsPassword"
              class="invalid"
            >
              min Length is 6
            </span>
          </template>
        </form-field-input>
      </template>

      <button
        type="submit"
        class="btn-success w-full flex justify-center items-center"
        :class="{'mb-5': !!message}"
      >
        <svg width="14" height="20" class="mr-2" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- eslint-disable-next-line max-len -->
            <path d="M1.90963 20H12.0904C12.5968 20 13.0826 19.7893 13.4407 19.4143C13.7988 19.0393 14 18.5307 14 18.0003V11.3326C14 10.8023 13.7988 10.2936 13.4407 9.91861C13.0826 9.5436 12.5968 9.33292 12.0904 9.33292H1.90963C1.40317 9.33292 0.917444 9.5436 0.559319 9.91861C0.201193 10.2936 0 10.8023 0 11.3326V18.0003C0 18.5307 0.201193 19.0393 0.559319 19.4143C0.917444 19.7893 1.40317 20 1.90963 20ZM12.0904 10.6702C12.2587 10.6702 12.4202 10.7398 12.5398 10.8638C12.6594 10.9879 12.7273 11.1564 12.7289 11.3326V18.0003C12.7289 18.1776 12.6616 18.3477 12.5419 18.4731C12.4221 18.5985 12.2597 18.669 12.0904 18.669H1.90963C1.74028 18.669 1.57787 18.5985 1.45812 18.4731C1.33837 18.3477 1.2711 18.1776 1.2711 18.0003V11.3326C1.2711 11.1553 1.33837 10.9852 1.45812 10.8598C1.57787 10.7344 1.74028 10.664 1.90963 10.664L12.0904 10.6702Z" fill="#173630"/>
            <!-- eslint-disable-next-line max-len -->
            <path d="M7 16.0131C7.16935 16.0131 7.33176 15.9427 7.45151 15.8173C7.57126 15.6919 7.63853 15.5218 7.63853 15.3445V13.9884C7.62321 13.8231 7.54964 13.6696 7.43214 13.5579C7.31465 13.4462 7.16166 13.3844 7.00298 13.3844C6.84431 13.3844 6.69132 13.4462 6.57383 13.5579C6.45633 13.6696 6.38276 13.8231 6.36743 13.9884V15.3445C6.36743 15.5207 6.43388 15.6899 6.55234 15.8151C6.6708 15.9403 6.83169 16.0115 7 16.0131Z" fill="#173630"/>
            <!-- eslint-disable-next-line max-len -->
            <path d="M3.18073 8.00187C3.26459 8.00187 3.34762 7.98458 3.42509 7.95098C3.50256 7.91737 3.57295 7.86812 3.63224 7.80603C3.69154 7.74394 3.73857 7.67023 3.77066 7.58911C3.80275 7.50799 3.81927 7.42104 3.81927 7.33323V4.6649C3.81927 3.78153 4.15438 2.93435 4.75088 2.30971C5.34739 1.68508 6.15642 1.33417 7 1.33417C7.84358 1.33417 8.65261 1.68508 9.24912 2.30971C9.84562 2.93435 10.1807 3.78153 10.1807 4.6649V7.33323C10.1721 7.42605 10.1822 7.51972 10.2102 7.60823C10.2382 7.69675 10.2836 7.77814 10.3435 7.84719C10.4033 7.91624 10.4763 7.97142 10.5577 8.00918C10.6392 8.04693 10.7272 8.06644 10.8163 8.06644C10.9053 8.06644 10.9934 8.04693 11.0748 8.00918C11.1563 7.97142 11.2292 7.91624 11.2891 7.84719C11.3489 7.77814 11.3943 7.69675 11.4224 7.60823C11.4504 7.51972 11.4604 7.42605 11.4518 7.33323V4.6649C11.4518 3.42769 10.9825 2.24115 10.147 1.36632C9.31161 0.491479 8.17851 0 6.99702 0C5.81553 0 4.68243 0.491479 3.84698 1.36632C3.01155 2.24115 2.5422 3.42769 2.5422 4.6649V7.33323C2.5422 7.42104 2.55872 7.50799 2.59081 7.58911C2.62289 7.67023 2.66993 7.74394 2.72922 7.80603C2.78852 7.86812 2.85891 7.91737 2.93638 7.95098C3.01385 7.98458 3.09688 8.00187 3.18073 8.00187Z" fill="#173630"/>
        </svg>
        {{ !hasToken ? 'Send Password Reset Link' : 'Reset Password' }}
      </button>
    </form>

    <div
      v-if="message"
      class="mt-3 bg-green-200 border border-green-600 rounded-xl p-4 md:py-5 md:px-8 font-medium text-sm text-green-600"
    >
      <span
        class="icon md:mr-3.5"
        v-html="icons.info"
      ></span>

      {{ message }}
    </div>
  </div>
</template>

<script>
import FormFieldInput from '@/components/form/FormFieldInput';
import { validationMixin } from 'vuelidate';
import {required, email, minLength, sameAs} from 'vuelidate/lib/validators';

export default {
  name: 'ResetPassword',

  components: {
    FormFieldInput
  },

  mixins: [validationMixin],

  validations() {
    if (this.hasToken) {
      return {
        email: {
          required,
          email
        },

        password: {
          required,
          minLength: minLength(6),
        },

        password_confirmation: {
          sameAsPassword: sameAs('password'),
        }
      }
    } else {
      return {
        email: {
          required,
          email
        }
      }
    }
  },

  props: {
    token: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    email: '',
    password: '12345678',
    password_confirmation: '12345678',
    message: '',
    errorMessage: ''
  }),

  mounted() {
    this.email = this.$route.query.email
  },

  methods: {
    onSubmit() {
      if (this.$v.$invalid) {
        this.$v.$touch()
        return
      }

      this.message = ''

      const formData = {
        email: this.email
      }

      let url = 'forgot-password'

      if (this.hasToken) {
        /* eslint-disable camelcase */
        const { password, token, password_confirmation } = this
        Object.assign(formData, {password, token, password_confirmation})
        url = 'reset-password'
      }

      this.$api.get('/sanctum/csrf-cookie')
          .then(() => {
            this.$api.post(`/${url}`, formData)
                .then(() => {
                  if (this.hasToken) {
                    this.$router.push({name: 'login'})
                  } else {
                    this.message = 'If your email is valid, you should receive a password reset email.'
                  }
                })
                .catch(err => {
                  this.errorMessage = (err.response && err.response.data && err.response.data.error) || ''
                })
          })

      return formData
    },
  },

  computed: {
    hasToken() {
      return !!this.token
    }
  }
};
</script>

<style scoped>

</style>
